import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { GoogleCalendarService } from 'app/services/google-calendar.service';
import { UserService } from 'app/services/user.service';
import { ModalConfirmComponent } from 'app/shared/modal-confirm/modal-confirm.component';
import { enterAnimation } from 'app/utils/animations.util';

@Component({
	selector: 'google-calendar-widget',
	templateUrl: './google-calendar.component.html',
	styleUrls: ['./google-calendar.component.scss'],
	animations: [enterAnimation],
})
export class GoogleCalendar {
	hasData = false;
	isWaitingNewData = false;
	isPremiumUser = false;
	isCalendarConnected = false;
	connectionURL = '';

	constructor(
		private user: UserService,
		private translate: TranslateService,
		private dialog: MatDialog,
		private gCalendarService: GoogleCalendarService,
		private notifications: ZkNotificationService
	) {
		this.user
			.checkSubscriptionAndContactTiers()
			.then(({ hasActiveSubscription }) => {
				if (hasActiveSubscription) {
					this.isPremiumUser = true;
					this.getCalendarData();
				}

				this.hasData = true;
			});
	}

	private getCalendarData() {
		const getStatusObs = this.gCalendarService.getStatus();
		getStatusObs.subscribe((response) => {
			this.isCalendarConnected = response.isActive;
			this.connectionURL = response.url;
		});

		return getStatusObs;
	}

	private getModalText(modalType: string): {
		title: string;
		info: string;
		btnText: string;
	} {
		const title = this.translate.instant(
			`GOOGLE_CALENDAR.MODALS.${modalType}.TITLE`
		);

		const btnText = this.translate.instant(
			`GOOGLE_CALENDAR.MODALS.${modalType}.BTN_CONTINUE`
		);

		let info = this.translate.instant(
			`GOOGLE_CALENDAR.MODALS.${modalType}.INFO`
		);

		if (modalType === 'CONNECTION') {
			const listAditionalInfo = `
			</br><ul>
			<li>${this.translate.instant(
				`GOOGLE_CALENDAR.MODALS.${modalType}.INFO_SYNC`
			)}</li>
			<li>${this.translate.instant(
				`GOOGLE_CALENDAR.MODALS.${modalType}.INFO_APP`
			)}</li>
			</ul>
			`;
			info += listAditionalInfo;
		}

		return { title, info, btnText };
	}

	private openModal() {
		const modalType = this.isCalendarConnected ? 'DISCONNECTION' : 'CONNECTION';
		const modalText = this.getModalText(modalType);
		const modal = this.dialog.open(ModalConfirmComponent, {
			data: {
				title: modalText.title,
				subtitle: modalText.info,
				labelButton: modalText.btnText,
				googleLogin: modalType === 'CONNECTION',
			},
		});

		modal.afterClosed().subscribe((anwser) => {
			if (anwser) {
				this.isWaitingNewData = true;
				modalType === 'CONNECTION'
					? this.connectCalendar()
					: this.disconnectCalendar();
			}
		});
	}

	private connectCalendar() {
		if (this.isPremiumUser) {
			if (this.connectionURL) {
				return this.handleConnectionTab(window.open(this.connectionURL));
			}

			return this.getCalendarData().subscribe((response) => {
				if (response.url) {
					this.handleConnectionTab(window.open(response.url));
				}
			});
		}

		this.notifications.showError(
			this.translate.instant(`GOOGLE_CALENDAR.ERRORS.NOT_PREMIUM`)
		);
	}

	private handleConnectionTab(connWindow: any) {
		this.isWaitingNewData = true;

		const timer = setInterval(() => {
			if (connWindow?.closed) {
				clearInterval(timer);
				this.getCalendarData();
				this.isWaitingNewData = false;
			}
		}, 1000);
	}

	private disconnectCalendar() {
		this.gCalendarService.removeIntegration().subscribe((response) => {
			this.isCalendarConnected = !response.success;
			this.isWaitingNewData = false;
		});
	}
}
